import {
  Search,
  Eye,
  Edit,
  Plus,
  ArrowRightLeft,
  Upload,
  Download,
  Settings,
  Image as ImageIcon,
  Trash2,
  X,
  Clock,
  CheckCircle,
} from 'lucide-react'
import { useState } from 'react'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'

interface InventoryItem {
  id: number
  name: string
  category: string
  stock: number
  stockCentro: number
  stockNorte: number
  stockSur: number
  minStock: number
  price: number
  cost: number
  branch: string
  lot: string
  expiry: string
  lastMovement: string
  supplier: string
  image?: string
}

interface PendingTransfer {
  id: string
  date: string
  fromBranch: string
  toBranch: string
  products: Array<{
    name: string
    quantity: number
  }>
  status: 'pending' | 'completed'
}

const pendingSends: PendingTransfer[] = [
  {
    id: 'TRF-001',
    date: '2026-04-27',
    fromBranch: 'Sucursal Centro',
    toBranch: 'Sucursal Norte',
    products: [
      { name: 'Paracetamol 500mg', quantity: 50 },
      { name: 'Ibuprofeno 400mg', quantity: 30 },
    ],
    status: 'pending',
  },
  {
    id: 'TRF-002',
    date: '2026-04-26',
    fromBranch: 'Sucursal Centro',
    toBranch: 'Sucursal Sur',
    products: [{ name: 'Loratadina 10mg', quantity: 25 }],
    status: 'pending',
  },
]

const pendingReceives: PendingTransfer[] = [
  {
    id: 'TRF-003',
    date: '2026-04-27',
    fromBranch: 'Sucursal Norte',
    toBranch: 'Sucursal Centro',
    products: [
      { name: 'Omeprazol 20mg', quantity: 40 },
      { name: 'Losartán 50mg', quantity: 20 },
    ],
    status: 'pending',
  },
  {
    id: 'TRF-004',
    date: '2026-04-26',
    fromBranch: 'Sucursal Sur',
    toBranch: 'Sucursal Centro',
    products: [{ name: 'Amoxicilina 500mg', quantity: 35 }],
    status: 'pending',
  },
]

const inventory: InventoryItem[] = [
  {
    id: 1,
    name: 'Paracetamol 500mg',
    category: 'Analgésicos',
    stock: 250,
    stockCentro: 250,
    stockNorte: 180,
    stockSur: 200,
    minStock: 50,
    price: 45.0,
    cost: 28.0,
    branch: 'Sucursal Centro',
    lot: 'LOT-2024-1245',
    expiry: '2026-12-15',
    lastMovement: '2026-04-25',
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 2,
    name: 'Ibuprofeno 400mg',
    category: 'Analgésicos',
    stock: 15,
    stockCentro: 15,
    stockNorte: 150,
    stockSur: 160,
    minStock: 30,
    price: 65.0,
    cost: 42.0,
    branch: 'Sucursal Centro',
    lot: 'LOT-2024-1892',
    expiry: '2027-03-20',
    lastMovement: '2026-04-26',
    supplier: 'Farmacéutica GNP',
  },
  {
    id: 3,
    name: 'Amoxicilina 500mg',
    category: 'Antibióticos',
    stock: 95,
    stockCentro: 50,
    stockNorte: 95,
    stockSur: 100,
    minStock: 40,
    price: 180.0,
    cost: 120.0,
    branch: 'Sucursal Norte',
    lot: 'LOT-2024-2103',
    expiry: '2026-08-10',
    lastMovement: '2026-04-24',
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 4,
    name: 'Loratadina 10mg',
    category: 'Antihistamínicos',
    stock: 140,
    stockCentro: 120,
    stockNorte: 140,
    stockSur: 140,
    minStock: 60,
    price: 120.0,
    cost: 75.0,
    branch: 'Sucursal Sur',
    lot: 'LOT-2024-1567',
    expiry: '2027-01-30',
    lastMovement: '2026-04-27',
    supplier: 'Farmacéutica GNP',
  },
  {
    id: 5,
    name: 'Omeprazol 20mg',
    category: 'Gastroenterología',
    stock: 200,
    stockCentro: 200,
    stockNorte: 160,
    stockSur: 150,
    minStock: 80,
    price: 95.0,
    cost: 58.0,
    branch: 'Sucursal Centro',
    lot: 'LOT-2024-1789',
    expiry: '2026-11-25',
    lastMovement: '2026-04-26',
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 6,
    name: 'Losartán 50mg',
    category: 'Cardiovascular',
    stock: 8,
    stockCentro: 25,
    stockNorte: 8,
    stockSur: 30,
    minStock: 25,
    price: 150.0,
    cost: 95.0,
    branch: 'Sucursal Norte',
    lot: 'LOT-2024-2245',
    expiry: '2026-10-05',
    lastMovement: '2026-04-23',
    supplier: 'Farmacéutica GNP',
  },
]

interface PricingTier {
  quantity: number
  margin: number
  discount: number
  price: string
  priceWithTax: string
}

interface NewProduct {
  name: string
  description: string
  barcode: string
  image: File | null
  category: string
  department: string
  productType: 'controlled' | 'antibiotic' | 'otc' | 'group4'
  lotNumber: string
  unitType: string
  costWithoutTax: string
  costWithTax: string
  price: string
  applyIVA: boolean
  applyIEPS: boolean
  expiryDate: string
  satKey: string
  minStock: string
  maxStock: string
  initialStock: string
  pricingTiers: PricingTier[]
}

export function Inventory() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedBranch, setSelectedBranch] = useState('Sucursal Centro')
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [showAddProductModal, setShowAddProductModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showReceiveConfirmModal, setShowReceiveConfirmModal] = useState(false)
  const [showSendReviewModal, setShowSendReviewModal] = useState(false)
  const [selectedTransfer, setSelectedTransfer] =
    useState<PendingTransfer | null>(null)
  const [sendProductsStatus, setSendProductsStatus] = useState<
    Record<
      string,
      {
        status: 'complete' | 'partial' | 'not_sent'
        quantity: number
        reason: string
      }
    >
  >({})
  const [receiveProductsStatus, setReceiveProductsStatus] = useState<
    Record<
      string,
      {
        status: 'received' | 'not_received'
        quantity: number
        reason: string
      }
    >
  >({})
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'subtract'>(
    'add',
  )
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('')
  const [adjustmentReason, setAdjustmentReason] = useState('')
  const [productSearchTerm, setProductSearchTerm] = useState('')

  const [newProduct, setNewProduct] = useState<NewProduct>({
    name: '',
    description: '',
    barcode: '',
    image: null,
    category: '',
    department: '',
    productType: 'otc',
    lotNumber: '',
    unitType: 'piezas',
    costWithoutTax: '',
    costWithTax: '',
    price: '',
    applyIVA: false,
    applyIEPS: false,
    expiryDate: '',
    satKey: '',
    minStock: '',
    maxStock: '',
    initialStock: '',
    pricingTiers: [],
  })

  const [transferAction, setTransferAction] = useState<
    'request' | 'receive' | 'send'
  >('send')
  const [transferProducts, setTransferProducts] = useState<
    Array<{
      productId: string
      quantity: string
    }>
  >([{ productId: '', quantity: '' }])
  const [transferData, setTransferData] = useState({
    fromBranch: '',
    toBranch: '',
    notes: '',
  })

  const [profitMargin, setProfitMargin] = useState(30)

  const categories = [
    'all',
    ...Array.from(new Set(inventory.map((i) => i.category))),
  ]
  const branches = [
    'all',
    ...Array.from(new Set(inventory.map((i) => i.branch))),
  ]

  const filteredInventory = inventory.filter((item) => {
    const matchesSearch = item.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
    const matchesCategory =
      selectedCategory === 'all' || item.category === selectedCategory
    const matchesBranch =
      selectedBranch === 'all' || item.branch === selectedBranch
    return matchesSearch && matchesCategory && matchesBranch
  })

  const getStockStatus = (stock: number, minStock: number) => {
    if (stock <= minStock * 0.5)
      return { label: 'Crítico', color: 'bg-red-100 text-red-700' }
    if (stock <= minStock)
      return { label: 'Bajo', color: 'bg-yellow-100 text-yellow-700' }
    return { label: 'Normal', color: 'bg-green-100 text-green-700' }
  }

  const openDetailModal = (item: InventoryItem) => {
    setSelectedItem(item)
    setShowDetailModal(true)
  }

  const openAdjustModal = (item: InventoryItem) => {
    setSelectedItem(item)
    setShowAdjustModal(true)
  }

  const applyAdjustment = () => {
    console.log('Ajuste aplicado:', {
      item: selectedItem?.name,
      type: adjustmentType,
      quantity: adjustmentQuantity,
      reason: adjustmentReason,
    })
    setShowAdjustModal(false)
    setAdjustmentQuantity('')
    setAdjustmentReason('')
  }

  const handleAddProduct = () => {
    console.log('Producto agregado:', newProduct)
    setShowAddProductModal(false)
    setNewProduct({
      name: '',
      description: '',
      barcode: '',
      image: null,
      department: '',
      productType: 'otc',
      lotNumber: '',
      unitType: 'piezas',
      cost: '',
      costIncludesIVA: false,
      price: '',
      applyIVA: false,
      applyIEPS: false,
      expiryDate: '',
      satKey: '',
      minStock: '',
      maxStock: '',
      initialStock: '',
    })
  }

  const handleTransfer = () => {
    console.log('Transferencia realizada:', {
      action: transferAction,
      products: transferProducts,
      ...transferData,
    })
    setShowTransferModal(false)
    setTransferProducts([{ productId: '', quantity: '' }])
    setTransferData({
      fromBranch: '',
      toBranch: '',
      notes: '',
    })
  }

  const addTransferProduct = () => {
    setTransferProducts([...transferProducts, { productId: '', quantity: '' }])
  }

  const removeTransferProduct = (index: number) => {
    setTransferProducts(transferProducts.filter((_, i) => i !== index))
  }

  const updateTransferProduct = (
    index: number,
    field: 'productId' | 'quantity',
    value: string,
  ) => {
    const updated = [...transferProducts]
    updated[index][field] = value
    setTransferProducts(updated)
  }

  const addProductFromSearch = (productId: string) => {
    // Si estamos en modo solicitud, verificar stock en sucursal origen
    if (transferAction === 'request' && transferData.fromBranch) {
      const item = inventory.find((i) => i.id.toString() === productId)
      if (!item) return

      // Obtener stock según la sucursal de origen
      let availableStock = 0
      if (transferData.fromBranch === 'centro')
        availableStock = item.stockCentro
      else if (transferData.fromBranch === 'norte')
        availableStock = item.stockNorte
      else if (transferData.fromBranch === 'sur') availableStock = item.stockSur

      if (availableStock <= 0) {
        alert(
          `El producto "${item.name}" no está disponible en ${transferData.fromBranch}. Stock: ${availableStock}`,
        )
        return
      }
    }

    const existingIndex = transferProducts.findIndex(
      (p) => p.productId === productId,
    )
    if (existingIndex >= 0) {
      // Si ya existe, incrementa la cantidad
      const updated = [...transferProducts]
      const currentQty = parseInt(updated[existingIndex].quantity) || 0
      updated[existingIndex].quantity = (currentQty + 1).toString()
      setTransferProducts(updated)
    } else {
      // Si no existe, agrégalo
      setTransferProducts([...transferProducts, { productId, quantity: '1' }])
    }
    setProductSearchTerm('') // Limpiar búsqueda
  }

  const handleReceiveConfirm = (transfer: PendingTransfer) => {
    setSelectedTransfer(transfer)
    // Inicializar todos los productos como recibidos completos
    const initialStatus: Record<
      string,
      {
        status: 'received' | 'not_received'
        quantity: number
        reason: string
      }
    > = {}
    transfer.products.forEach((product, idx) => {
      initialStatus[idx.toString()] = {
        status: 'received',
        quantity: product.quantity,
        reason: '',
      }
    })
    setReceiveProductsStatus(initialStatus)
    setShowReceiveConfirmModal(true)
  }

  const handleSendReview = (transfer: PendingTransfer) => {
    setSelectedTransfer(transfer)
    // Inicializar estado de productos basado en stock disponible
    const initialStatus: Record<
      string,
      {
        status: 'complete' | 'partial' | 'not_sent'
        quantity: number
        reason: string
      }
    > = {}
    transfer.products.forEach((product, idx) => {
      // Buscar el producto en inventario para verificar stock
      const inventoryItem = inventory.find((i) => i.name === product.name)
      const availableStock = inventoryItem?.stock || 0

      if (availableStock >= product.quantity) {
        initialStatus[idx.toString()] = {
          status: 'complete',
          quantity: product.quantity,
          reason: '',
        }
      } else if (availableStock > 0) {
        initialStatus[idx.toString()] = {
          status: 'partial',
          quantity: availableStock,
          reason: `Stock insuficiente. Disponible: ${availableStock}`,
        }
      } else {
        initialStatus[idx.toString()] = {
          status: 'not_sent',
          quantity: 0,
          reason: 'Sin stock disponible',
        }
      }
    })
    setSendProductsStatus(initialStatus)
    setShowSendReviewModal(true)
  }

  const confirmReceive = () => {
    console.log(
      'Confirmando recepción:',
      selectedTransfer,
      receiveProductsStatus,
    )
    setShowReceiveConfirmModal(false)
    setSelectedTransfer(null)
    setReceiveProductsStatus({})
  }

  const confirmSend = () => {
    console.log('Confirmando envío:', selectedTransfer, sendProductsStatus)
    setShowSendReviewModal(false)
    setSelectedTransfer(null)
    setSendProductsStatus({})
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setNewProduct({ ...newProduct, image: file })
    }
  }

  const handleExport = () => {
    console.log('Exportando productos a Excel...')
  }

  const calculateFinalPrice = () => {
    const basePrice = parseFloat(newProduct.price) || 0
    let finalPrice = basePrice

    if (newProduct.applyIVA) {
      finalPrice += basePrice * 0.16
    }

    if (newProduct.applyIEPS) {
      finalPrice += basePrice * 0.08
    }

    return finalPrice.toFixed(2)
  }

  const calculatePriceFromMargin = (margin: number) => {
    const baseCost = parseFloat(newProduct.costWithoutTax) || 0

    // Aplicamos el margen de utilidad sobre el costo sin impuestos
    const price = baseCost * (1 + margin / 100)
    setNewProduct({ ...newProduct, price: price.toFixed(2) })
  }

  const handleProfitMarginChange = (margin: number) => {
    if (newProduct.costWithoutTax) {
      calculatePriceFromMargin(margin)
    }
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Inventario</h1>
          <p className="mt-1 text-sm text-slate-600">
            {filteredInventory.length} productos encontrados
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTransferModal(true)}>
            <ArrowRightLeft className="mr-2 h-4 w-4" />
            Transferencias
          </Button>
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Importar
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Exportar
          </Button>
          <Button onClick={() => setShowAddProductModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Agregar Producto
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar producto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories
                .filter((c) => c !== 'all')
                .map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger>
              <SelectValue placeholder="Sucursal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las sucursales</SelectItem>
              {branches
                .filter((b) => b !== 'all')
                .map((branch) => (
                  <SelectItem key={branch} value={branch}>
                    {branch}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                  Imagen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                  Producto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                  Categoría
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                  Precio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-slate-700 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredInventory.map((item) => {
                const status = getStockStatus(item.stock, item.minStock)
                return (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-12 w-12 rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded bg-slate-100">
                          <ImageIcon className="h-6 w-6 text-slate-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900">
                        {item.name}
                      </div>
                      <div className="text-sm text-slate-500">
                        Lote: {item.lot}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">
                      {item.category}
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-0.5 text-xs">
                        <div
                          className={
                            item.branch === 'Sucursal Centro' ? 'font-bold' : ''
                          }
                        >
                          <span className="text-slate-600">Centro:</span>{' '}
                          {item.stockCentro}
                        </div>
                        {item.branch === 'Sucursal Centro' && (
                          <div className="text-xs text-slate-500">
                            Mín: {item.minStock}
                          </div>
                        )}
                        <div
                          className={
                            item.branch === 'Sucursal Norte' ? 'font-bold' : ''
                          }
                        >
                          <span className="text-slate-600">Norte:</span>{' '}
                          {item.stockNorte}
                        </div>
                        {item.branch === 'Sucursal Norte' && (
                          <div className="text-xs text-slate-500">
                            Mín: {item.minStock}
                          </div>
                        )}
                        <div
                          className={
                            item.branch === 'Sucursal Sur' ? 'font-bold' : ''
                          }
                        >
                          <span className="text-slate-600">Sur:</span>{' '}
                          {item.stockSur}
                        </div>
                        {item.branch === 'Sucursal Sur' && (
                          <div className="text-xs text-slate-500">
                            Mín: {item.minStock}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={status.color}>{status.label}</Badge>
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900">
                      ${item.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDetailModal(item)}
                          className="rounded p-1 hover:bg-slate-100"
                        >
                          <Eye className="h-4 w-4 text-slate-600" />
                        </button>
                        <button
                          onClick={() => openAdjustModal(item)}
                          className="rounded p-1 hover:bg-slate-100"
                        >
                          <Edit className="h-4 w-4 text-slate-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalles del Producto</DialogTitle>
            <DialogDescription className="sr-only">
              Información completa del producto incluyendo stock, lote y
              caducidad
            </DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 p-4">
                <h3 className="mb-3 font-semibold text-slate-900">
                  {selectedItem.name}
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-600">Categoría:</span>
                    <p className="font-medium text-slate-900">
                      {selectedItem.category}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Sucursal:</span>
                    <p className="font-medium text-slate-900">
                      {selectedItem.branch}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Stock actual:</span>
                    <p className="font-medium text-slate-900">
                      {selectedItem.stock} unidades
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Stock mínimo:</span>
                    <p className="font-medium text-slate-900">
                      {selectedItem.minStock} unidades
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Lote:</span>
                    <p className="font-medium text-slate-900">
                      {selectedItem.lot}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Caducidad:</span>
                    <p className="font-medium text-slate-900">
                      {selectedItem.expiry}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Precio venta:</span>
                    <p className="font-medium text-slate-900">
                      ${selectedItem.price.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Costo:</span>
                    <p className="font-medium text-slate-900">
                      ${selectedItem.cost.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Proveedor:</span>
                    <p className="font-medium text-slate-900">
                      {selectedItem.supplier}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Último movimiento:</span>
                    <p className="font-medium text-slate-900">
                      {selectedItem.lastMovement}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-900">
                  <strong>Margen de utilidad:</strong>{' '}
                  {(
                    ((selectedItem.price - selectedItem.cost) /
                      selectedItem.cost) *
                    100
                  ).toFixed(1)}
                  %
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Modal */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ajustar Stock</DialogTitle>
            <DialogDescription className="sr-only">
              Agregar o restar unidades del inventario con motivo
            </DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-sm font-medium text-slate-900">
                  {selectedItem.name}
                </p>
                <p className="mt-1 text-xs text-slate-600">
                  Stock actual: {selectedItem.stock} unidades
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Tipo de ajuste:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setAdjustmentType('add')}
                    className={`rounded-lg border-2 p-3 transition-all ${
                      adjustmentType === 'add'
                        ? 'border-green-500 bg-green-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Plus className="mx-auto mb-1 h-5 w-5" />
                    <p className="text-xs font-medium">Agregar</p>
                  </button>
                  <button
                    onClick={() => setAdjustmentType('subtract')}
                    className={`rounded-lg border-2 p-3 transition-all ${
                      adjustmentType === 'subtract'
                        ? 'border-red-500 bg-red-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Settings className="mx-auto mb-1 h-5 w-5" />
                    <p className="text-xs font-medium">Restar</p>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Cantidad:
                </label>
                <Input
                  type="number"
                  value={adjustmentQuantity}
                  onChange={(e) => setAdjustmentQuantity(e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Motivo:
                </label>
                <Input
                  type="text"
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  placeholder="Ej: Merma, Devolución, Corrección"
                />
              </div>

              {adjustmentQuantity && (
                <div className="rounded-lg bg-blue-50 p-3">
                  <p className="text-sm text-blue-900">
                    Nuevo stock:{' '}
                    <strong>
                      {adjustmentType === 'add'
                        ? selectedItem.stock + parseInt(adjustmentQuantity)
                        : selectedItem.stock -
                          parseInt(adjustmentQuantity)}{' '}
                      unidades
                    </strong>
                  </p>
                </div>
              )}

              <Button
                onClick={applyAdjustment}
                disabled={!adjustmentQuantity || !adjustmentReason}
                className="w-full"
              >
                Aplicar Ajuste
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Product Modal */}
      <Dialog open={showAddProductModal} onOpenChange={setShowAddProductModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Agregar Nuevo Producto</DialogTitle>
            <DialogDescription className="sr-only">
              Formulario completo para registrar un nuevo producto en el
              inventario
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Nombre del Producto <span className="text-red-600">*</span>
                </label>
                <Input
                  value={newProduct.name}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, name: e.target.value })
                  }
                  placeholder="Ej: Paracetamol 500mg"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Código de Barras
                </label>
                <Input
                  value={newProduct.barcode}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, barcode: e.target.value })
                  }
                  placeholder="7501234567890"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Descripción
              </label>
              <Textarea
                value={newProduct.description}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, description: e.target.value })
                }
                placeholder="Descripción detallada del producto..."
                rows={3}
              />
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Imagen del Producto
              </label>
              <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="product-image"
                />
                <label htmlFor="product-image" className="cursor-pointer">
                  <ImageIcon className="mx-auto mb-2 h-12 w-12 text-slate-400" />
                  <p className="text-sm text-slate-600">
                    {newProduct.image
                      ? newProduct.image.name
                      : 'Haz clic para subir imagen'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    PNG, JPG hasta 5MB
                  </p>
                </label>
              </div>
            </div>

            {/* Category, Department and Classification */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Categoría <span className="text-red-600">*</span>
                </label>
                <Select
                  value={newProduct.category}
                  onValueChange={(value) =>
                    setNewProduct({ ...newProduct, category: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medicamentos">Medicamentos</SelectItem>
                    <SelectItem value="vitaminas">
                      Vitaminas y Suplementos
                    </SelectItem>
                    <SelectItem value="dermocosmetica">
                      Dermocosmética
                    </SelectItem>
                    <SelectItem value="higiene">Higiene Personal</SelectItem>
                    <SelectItem value="bebes">Bebés y Maternidad</SelectItem>
                    <SelectItem value="nutricion">Nutrición</SelectItem>
                    <SelectItem value="otros">Otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Departamento <span className="text-red-600">*</span>
                </label>
                <Select
                  value={newProduct.department}
                  onValueChange={(value) =>
                    setNewProduct({ ...newProduct, department: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="antibioticos">Antibióticos</SelectItem>
                    <SelectItem value="antigripales">Antigripales</SelectItem>
                    <SelectItem value="analgesicos">Analgésicos</SelectItem>
                    <SelectItem value="cardiovascular">
                      Cardiovascular
                    </SelectItem>
                    <SelectItem value="gastrointestinal">
                      Gastrointestinal
                    </SelectItem>
                    <SelectItem value="dermatologia">Dermatología</SelectItem>
                    <SelectItem value="vitaminas">
                      Vitaminas y Suplementos
                    </SelectItem>
                    <SelectItem value="cuidado-personal">
                      Cuidado Personal
                    </SelectItem>
                    <SelectItem value="otros">Otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Tipo de Producto <span className="text-red-600">*</span>
                </label>
                <Select
                  value={newProduct.productType}
                  onValueChange={(
                    value: 'controlled' | 'antibiotic' | 'otc' | 'group4',
                  ) => setNewProduct({ ...newProduct, productType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="otc">Venta Libre</SelectItem>
                    <SelectItem value="antibiotic">Antibiótico</SelectItem>
                    <SelectItem value="controlled">
                      Medicamento Controlado
                    </SelectItem>
                    <SelectItem value="group4">Grupo 4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lot Number and Expiry - Only for Antibiotics and Controlled */}
            {(newProduct.productType === 'antibiotic' ||
              newProduct.productType === 'controlled') && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-yellow-900">
                      Número de Lote <span className="text-red-600">*</span>
                    </label>
                    <Input
                      value={newProduct.lotNumber}
                      onChange={(e) =>
                        setNewProduct({
                          ...newProduct,
                          lotNumber: e.target.value,
                        })
                      }
                      placeholder="LOT-2024-XXXX"
                    />
                    <p className="text-xs text-yellow-700">
                      Requerido para{' '}
                      {newProduct.productType === 'antibiotic'
                        ? 'antibióticos'
                        : 'medicamentos controlados'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-yellow-900">
                      Fecha de Caducidad <span className="text-red-600">*</span>
                    </label>
                    <Input
                      type="date"
                      value={newProduct.expiryDate}
                      onChange={(e) =>
                        setNewProduct({
                          ...newProduct,
                          expiryDate: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Note for Group 4 */}
            {newProduct.productType === 'group4' && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="mb-1 text-sm font-medium text-blue-900">
                  Producto Grupo 4
                </p>
                <p className="text-xs text-blue-700">
                  Este tipo de producto requiere solicitud de receta médica pero
                  no requiere número de lote ni fecha de caducidad.
                </p>
              </div>
            )}

            {/* Tax Options */}
            <div className="space-y-3 rounded-lg bg-slate-50 p-4">
              <h4 className="text-sm font-medium text-slate-900">
                Impuestos para Precio Final al Público
              </h4>
              <p className="mb-3 text-xs text-slate-600">
                Selecciona un impuesto que se aplicará al precio de venta para
                obtener el precio final
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="apply-iva"
                    checked={newProduct.applyIVA}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setNewProduct({
                          ...newProduct,
                          applyIVA: true,
                          applyIEPS: false,
                        })
                      } else {
                        setNewProduct({ ...newProduct, applyIVA: false })
                      }
                    }}
                  />
                  <label
                    htmlFor="apply-iva"
                    className="cursor-pointer text-sm text-slate-700"
                  >
                    Agregar IVA (16%) al precio final
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="apply-ieps"
                    checked={newProduct.applyIEPS}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setNewProduct({
                          ...newProduct,
                          applyIEPS: true,
                          applyIVA: false,
                        })
                      } else {
                        setNewProduct({ ...newProduct, applyIEPS: false })
                      }
                    }}
                  />
                  <label
                    htmlFor="apply-ieps"
                    className="cursor-pointer text-sm text-slate-700"
                  >
                    Agregar IEPS (8%) al precio final
                  </label>
                </div>
              </div>
            </div>

            {/* Cost Fields */}
            <div className="space-y-3 rounded-lg bg-blue-50 p-4">
              <h4 className="text-sm font-medium text-blue-900">
                Costo del Producto
              </h4>
              <p className="mb-3 text-xs text-blue-700">
                Ingresa el costo con o sin impuesto, el otro campo se calculará
                automáticamente
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-blue-900">
                    Costo sin Impuesto <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newProduct.costWithoutTax}
                    onChange={(e) => {
                      const value = e.target.value
                      setNewProduct({ ...newProduct, costWithoutTax: value })

                      // Calculate cost with tax
                      if (value) {
                        const costValue = parseFloat(value)
                        let taxedCost = costValue
                        if (newProduct.applyIVA) taxedCost *= 1.16
                        if (newProduct.applyIEPS) taxedCost *= 1.08
                        setNewProduct({
                          ...newProduct,
                          costWithoutTax: value,
                          costWithTax: taxedCost.toFixed(2),
                        })
                      } else {
                        setNewProduct({
                          ...newProduct,
                          costWithoutTax: value,
                          costWithTax: '',
                        })
                      }
                    }}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-blue-900">
                    Costo con Impuesto <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newProduct.costWithTax}
                    onChange={(e) => {
                      const value = e.target.value
                      setNewProduct({ ...newProduct, costWithTax: value })

                      // Calculate cost without tax
                      if (value) {
                        const costValue = parseFloat(value)
                        let untaxedCost = costValue
                        if (newProduct.applyIVA) untaxedCost /= 1.16
                        if (newProduct.applyIEPS) untaxedCost /= 1.08
                        setNewProduct({
                          ...newProduct,
                          costWithTax: value,
                          costWithoutTax: untaxedCost.toFixed(2),
                        })
                      } else {
                        setNewProduct({
                          ...newProduct,
                          costWithTax: value,
                          costWithoutTax: '',
                        })
                      }
                    }}
                    placeholder="0.00"
                  />
                </div>
              </div>
              {!newProduct.applyIVA && !newProduct.applyIEPS && (
                <p className="rounded bg-orange-100 p-2 text-xs text-orange-700">
                  Selecciona un impuesto arriba para habilitar el cálculo
                  automático
                </p>
              )}
            </div>

            {/* Unit Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Tipo de Unidad
              </label>
              <Select
                value={newProduct.unitType}
                onValueChange={(value) =>
                  setNewProduct({ ...newProduct, unitType: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="piezas">Piezas</SelectItem>
                  <SelectItem value="cajas">Cajas</SelectItem>
                  <SelectItem value="frascos">Frascos</SelectItem>
                  <SelectItem value="sobres">Sobres</SelectItem>
                  <SelectItem value="tubos">Tubos</SelectItem>
                  <SelectItem value="ampolletas">Ampolletas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Profit Margin Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Porcentaje de Utilidad
              </label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={0}
                  max={500}
                  step={1}
                  value={profitMargin}
                  onChange={(e) => {
                    const margin = parseFloat(e.target.value) || 0
                    setProfitMargin(margin)
                  }}
                  onBlur={() => {
                    if (newProduct.costWithoutTax && profitMargin > 0) {
                      calculatePriceFromMargin(profitMargin)
                    }
                  }}
                  placeholder="30"
                  className="flex-1"
                />
                <div className="flex items-center gap-1">
                  <span className="text-2xl font-semibold text-blue-700">
                    {profitMargin}%
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-600">
                Ingresa el porcentaje de ganancia sobre el costo sin impuesto
              </p>
            </div>

            {/* Price Display */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Precio de Venta Base
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newProduct.price}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, price: e.target.value })
                    }
                    placeholder="0.00"
                  />
                  <p className="text-xs text-slate-600">
                    Calculado automáticamente según el % de utilidad
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Precio Final al Público
                  </label>
                  <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                    <p className="text-2xl font-semibold text-green-700">
                      ${calculateFinalPrice()}
                    </p>
                    <div className="mt-1 space-y-0.5 text-xs text-green-600">
                      {newProduct.price && (
                        <p>Base: ${parseFloat(newProduct.price).toFixed(2)}</p>
                      )}
                      {newProduct.applyIVA && newProduct.price && (
                        <p>
                          + IVA (16%): $
                          {(parseFloat(newProduct.price) * 0.16).toFixed(2)}
                        </p>
                      )}
                      {newProduct.applyIEPS && newProduct.price && (
                        <p>
                          + IEPS (8%): $
                          {(
                            parseFloat(newProduct.price) *
                            (newProduct.applyIVA ? 1.16 : 1) *
                            0.08
                          ).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Calculation Summary */}
              {newProduct.costWithoutTax && newProduct.price && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <h5 className="mb-2 text-sm font-medium text-blue-900">
                    Resumen de Cálculo
                  </h5>
                  <div className="space-y-1 text-xs text-blue-800">
                    <p>Costo sin impuesto: ${newProduct.costWithoutTax}</p>
                    {newProduct.costWithTax && (
                      <p>Costo con impuesto: ${newProduct.costWithTax}</p>
                    )}
                    <p>Utilidad: {profitMargin}%</p>
                    <p>Precio de venta base: ${newProduct.price}</p>
                    <p className="border-t border-blue-300 pt-1 font-semibold">
                      Precio final al público: ${calculateFinalPrice()}
                    </p>
                  </div>
                </div>
              )}

              {/* Pricing Tiers by Quantity */}
              <div className="mt-4 space-y-3 rounded-lg border border-purple-200 bg-purple-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-sm font-medium text-purple-900">
                      Precios por Cantidad
                    </h5>
                    <p className="mt-1 text-xs text-purple-700">
                      Configura diferentes precios, utilidades y descuentos
                      según la cantidad vendida
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setNewProduct({
                        ...newProduct,
                        pricingTiers: [
                          ...newProduct.pricingTiers,
                          {
                            quantity: 1,
                            margin: 0,
                            discount: 0,
                            price: '',
                            priceWithTax: '',
                          },
                        ],
                      })
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Agregar Nivel
                  </Button>
                </div>

                {newProduct.pricingTiers.length > 0 && (
                  <div className="space-y-2">
                    {newProduct.pricingTiers.map((tier, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-6 items-center gap-2 rounded-lg bg-white p-3"
                      >
                        <div className="space-y-1">
                          <label className="text-xs text-slate-600">
                            Cantidad
                          </label>
                          <Input
                            type="number"
                            min={1}
                            value={tier.quantity}
                            onChange={(e) => {
                              const tiers = [...newProduct.pricingTiers]
                              tiers[index].quantity =
                                parseInt(e.target.value) || 1
                              setNewProduct({
                                ...newProduct,
                                pricingTiers: tiers,
                              })
                            }}
                            className="h-8"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-slate-600">
                            Utilidad %
                          </label>
                          <Input
                            type="number"
                            min={0}
                            max={500}
                            value={tier.margin}
                            onChange={(e) => {
                              const tiers = [...newProduct.pricingTiers]
                              const margin = parseFloat(e.target.value) || 0
                              tiers[index].margin = margin

                              // Calculate price based on margin using cost without tax
                              const baseCost =
                                parseFloat(newProduct.costWithoutTax) || 0
                              const calculatedPrice =
                                baseCost * (1 + margin / 100)
                              tiers[index].price = calculatedPrice.toFixed(2)

                              // Calculate price with tax
                              let priceWithTax = calculatedPrice
                              if (newProduct.applyIVA) priceWithTax *= 1.16
                              if (newProduct.applyIEPS) priceWithTax *= 1.08
                              tiers[index].priceWithTax =
                                priceWithTax.toFixed(2)

                              setNewProduct({
                                ...newProduct,
                                pricingTiers: tiers,
                              })
                            }}
                            className="h-8"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-slate-600">
                            Descuento %
                          </label>
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            value={tier.discount}
                            onChange={(e) => {
                              const tiers = [...newProduct.pricingTiers]
                              const discount = parseFloat(e.target.value) || 0
                              tiers[index].discount = discount

                              // Apply discount to price
                              if (tier.price) {
                                const basePrice = parseFloat(tier.price)
                                const discountedPrice =
                                  basePrice * (1 - discount / 100)

                                // Calculate price with tax
                                let priceWithTax = discountedPrice
                                if (newProduct.applyIVA) priceWithTax *= 1.16
                                if (newProduct.applyIEPS) priceWithTax *= 1.08
                                tiers[index].priceWithTax =
                                  priceWithTax.toFixed(2)
                              }

                              setNewProduct({
                                ...newProduct,
                                pricingTiers: tiers,
                              })
                            }}
                            className="h-8"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-slate-600">
                            Precio
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.price}
                            onChange={(e) => {
                              const tiers = [...newProduct.pricingTiers]
                              const price = e.target.value
                              tiers[index].price = price

                              // Calculate price with tax
                              if (price) {
                                const basePrice = parseFloat(price)
                                const discountedPrice =
                                  basePrice * (1 - tier.discount / 100)
                                let priceWithTax = discountedPrice
                                if (newProduct.applyIVA) priceWithTax *= 1.16
                                if (newProduct.applyIEPS) priceWithTax *= 1.08
                                tiers[index].priceWithTax =
                                  priceWithTax.toFixed(2)
                              } else {
                                tiers[index].priceWithTax = ''
                              }

                              setNewProduct({
                                ...newProduct,
                                pricingTiers: tiers,
                              })
                            }}
                            className="h-8"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-slate-600">
                            Precio c/Impuesto
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.priceWithTax}
                            readOnly
                            className="h-8 bg-slate-50"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const tiers = newProduct.pricingTiers.filter(
                                (_, i) => i !== index,
                              )
                              setNewProduct({
                                ...newProduct,
                                pricingTiers: tiers,
                              })
                            }}
                            className="h-8"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {newProduct.pricingTiers.length === 0 && (
                  <p className="py-2 text-center text-xs text-purple-600">
                    Sin niveles de precio configurados. Haz clic en "Agregar
                    Nivel" para comenzar.
                  </p>
                )}
              </div>
            </div>

            {/* Stock Configuration */}
            <div className="space-y-3 rounded-lg bg-slate-50 p-4">
              <h4 className="text-sm font-medium text-slate-900">
                Configuración de Stock
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Stock Mínimo <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={newProduct.minStock}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, minStock: e.target.value })
                    }
                    placeholder="0"
                  />
                  <p className="text-xs text-slate-600">Alerta de stock bajo</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Stock Máximo <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={newProduct.maxStock}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, maxStock: e.target.value })
                    }
                    placeholder="0"
                  />
                  <p className="text-xs text-slate-600">Capacidad máxima</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Stock Inicial <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={newProduct.initialStock}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        initialStock: e.target.value,
                      })
                    }
                    placeholder="0"
                  />
                  <p className="text-xs text-slate-600">Cantidad de entrada</p>
                </div>
              </div>
            </div>

            {/* SAT Key - Searchable Select */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Clave SAT <span className="text-red-600">*</span>
              </label>
              <Select
                value={newProduct.satKey}
                onValueChange={(value) =>
                  setNewProduct({ ...newProduct, satKey: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Buscar y seleccionar clave SAT" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="51121701">
                    51121701 - Analgésicos
                  </SelectItem>
                  <SelectItem value="51121702">
                    51121702 - Antiinflamatorios
                  </SelectItem>
                  <SelectItem value="51101506">
                    51101506 - Antibióticos (penicilinas)
                  </SelectItem>
                  <SelectItem value="51101507">
                    51101507 - Antibióticos (cefalosporinas)
                  </SelectItem>
                  <SelectItem value="51101801">
                    51101801 - Antihistamínicos
                  </SelectItem>
                  <SelectItem value="51171701">
                    51171701 - Antiulcerosos (inhibidores de bomba de protones)
                  </SelectItem>
                  <SelectItem value="51171501">
                    51171501 - Antidiarreicos
                  </SelectItem>
                  <SelectItem value="51141602">
                    51141602 - Antihipertensivos
                  </SelectItem>
                  <SelectItem value="51181602">
                    51181602 - Antidiabéticos orales
                  </SelectItem>
                  <SelectItem value="51151603">
                    51151603 - Vitaminas y minerales
                  </SelectItem>
                  <SelectItem value="51151501">
                    51151501 - Suplementos alimenticios
                  </SelectItem>
                  <SelectItem value="53131626">
                    53131626 - Cremas y pomadas dermatológicas
                  </SelectItem>
                  <SelectItem value="42151501">
                    42151501 - Productos para el cuidado de la piel
                  </SelectItem>
                  <SelectItem value="42151502">
                    42151502 - Productos para el cuidado del cabello
                  </SelectItem>
                  <SelectItem value="53121601">
                    53121601 - Jeringas y agujas
                  </SelectItem>
                  <SelectItem value="42294801">42294801 - Pañales</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-600">
                Selecciona la clave del SAT correspondiente al producto
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 border-t border-slate-200 pt-4">
              <Button onClick={handleAddProduct} className="flex-1">
                Agregar Producto
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAddProductModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Gestión de Transferencias</DialogTitle>
            <DialogDescription className="sr-only">
              Solicitar, recibir o enviar productos entre sucursales
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Transfer Action Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Tipo de Operación:
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setTransferAction('send')}
                  className={`rounded-lg border-2 p-3 transition-all ${
                    transferAction === 'send'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <ArrowRightLeft className="mx-auto mb-1 h-5 w-5 text-blue-600" />
                  <p className="text-xs font-medium">Enviar</p>
                  <p className="text-xs text-slate-600">A otra sucursal</p>
                </button>
                <button
                  onClick={() => setTransferAction('request')}
                  className={`rounded-lg border-2 p-3 transition-all ${
                    transferAction === 'request'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Download className="mx-auto mb-1 h-5 w-5 text-green-600" />
                  <p className="text-xs font-medium">Solicitar</p>
                  <p className="text-xs text-slate-600">De otra sucursal</p>
                </button>
                <button
                  onClick={() => setTransferAction('receive')}
                  className={`rounded-lg border-2 p-3 transition-all ${
                    transferAction === 'receive'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Upload className="mx-auto mb-1 h-5 w-5 text-purple-600" />
                  <p className="text-xs font-medium">Recibir</p>
                  <p className="text-xs text-slate-600">De otra sucursal</p>
                </button>
              </div>
            </div>

            {/* Pending Sends - Only show for Send action */}
            {transferAction === 'send' && pendingSends.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  <h4 className="text-sm font-medium text-slate-900">
                    Pendientes de Envío
                  </h4>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-orange-200 bg-orange-50 p-3">
                  {pendingSends.map((transfer) => (
                    <div
                      key={transfer.id}
                      className="mb-2 rounded-lg bg-white p-3 last:mb-0"
                    >
                      <div className="mb-2 flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {transfer.id}
                          </p>
                          <p className="text-xs text-slate-600">
                            {transfer.date}
                          </p>
                        </div>
                        <Badge className="bg-orange-100 text-orange-700">
                          Pendiente
                        </Badge>
                      </div>
                      <p className="mb-2 text-xs text-slate-700">
                        De: {transfer.fromBranch} → A: {transfer.toBranch}
                      </p>
                      <div className="space-y-1">
                        {transfer.products.map((product, idx) => (
                          <p key={idx} className="text-xs text-slate-600">
                            • {product.name} ({product.quantity} uds)
                          </p>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        className="mt-2 w-full"
                        variant="outline"
                        onClick={() => handleSendReview(transfer)}
                      >
                        <Eye className="mr-1 h-3 w-3" />
                        Revisar Solicitud
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Receives - Only show for Receive action */}
            {transferAction === 'receive' && pendingReceives.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-purple-600" />
                  <h4 className="text-sm font-medium text-slate-900">
                    Pendientes por Recibir
                  </h4>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-purple-200 bg-purple-50 p-3">
                  {pendingReceives.map((transfer) => (
                    <div
                      key={transfer.id}
                      className="mb-2 rounded-lg bg-white p-3 last:mb-0"
                    >
                      <div className="mb-2 flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {transfer.id}
                          </p>
                          <p className="text-xs text-slate-600">
                            {transfer.date}
                          </p>
                        </div>
                        <Badge className="bg-purple-100 text-purple-700">
                          Por Recibir
                        </Badge>
                      </div>
                      <p className="mb-2 text-xs text-slate-700">
                        De: {transfer.fromBranch} → A: {transfer.toBranch}
                      </p>
                      <div className="space-y-1">
                        {transfer.products.map((product, idx) => (
                          <p key={idx} className="text-xs text-slate-600">
                            • {product.name} ({product.quantity} uds)
                          </p>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        className="mt-2 w-full"
                        variant="outline"
                        onClick={() => handleReceiveConfirm(transfer)}
                      >
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Confirmar Recepción
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Branch Selection - Only for Send and Request */}
            {(transferAction === 'send' || transferAction === 'request') && (
              <div className="grid grid-cols-2 gap-4">
                {transferAction === 'send' && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Desde (Mi Sucursal)
                      </label>
                      <Select
                        value={transferData.fromBranch}
                        onValueChange={(value) =>
                          setTransferData({
                            ...transferData,
                            fromBranch: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sucursal origen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="centro">
                            Sucursal Centro
                          </SelectItem>
                          <SelectItem value="norte">Sucursal Norte</SelectItem>
                          <SelectItem value="sur">Sucursal Sur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Hacia
                      </label>
                      <Select
                        value={transferData.toBranch}
                        onValueChange={(value) =>
                          setTransferData({ ...transferData, toBranch: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sucursal destino" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="centro">
                            Sucursal Centro
                          </SelectItem>
                          <SelectItem value="norte">Sucursal Norte</SelectItem>
                          <SelectItem value="sur">Sucursal Sur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {transferAction === 'request' && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Solicitar De
                      </label>
                      <Select
                        value={transferData.fromBranch}
                        onValueChange={(value) =>
                          setTransferData({
                            ...transferData,
                            fromBranch: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sucursal origen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="centro">
                            Sucursal Centro
                          </SelectItem>
                          <SelectItem value="norte">Sucursal Norte</SelectItem>
                          <SelectItem value="sur">Sucursal Sur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Para (Mi Sucursal)
                      </label>
                      <Select
                        value={transferData.toBranch}
                        onValueChange={(value) =>
                          setTransferData({ ...transferData, toBranch: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Mi sucursal" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="centro">
                            Sucursal Centro
                          </SelectItem>
                          <SelectItem value="norte">Sucursal Norte</SelectItem>
                          <SelectItem value="sur">Sucursal Sur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Products Selection - Only for Send and Request */}
            {(transferAction === 'send' || transferAction === 'request') && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">
                  Productos:
                </label>

                {/* Product Search */}
                <div className="relative">
                  <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    placeholder="Buscar por nombre o código de barras..."
                    className="pl-10"
                  />
                  {/* Search Results Dropdown */}
                  {productSearchTerm && (
                    <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                      {inventory
                        .filter(
                          (item) =>
                            item.name
                              .toLowerCase()
                              .includes(productSearchTerm.toLowerCase()) ||
                            (item.lot &&
                              item.lot
                                .toLowerCase()
                                .includes(productSearchTerm.toLowerCase())),
                        )
                        .map((item) => {
                          // Calcular stock disponible según modo y sucursal
                          let displayStock = item.stock
                          let isAvailable = true

                          if (
                            transferAction === 'request' &&
                            transferData.fromBranch
                          ) {
                            if (transferData.fromBranch === 'centro')
                              displayStock = item.stockCentro
                            else if (transferData.fromBranch === 'norte')
                              displayStock = item.stockNorte
                            else if (transferData.fromBranch === 'sur')
                              displayStock = item.stockSur

                            isAvailable = displayStock > 0
                          }

                          return (
                            <button
                              key={item.id}
                              onClick={() =>
                                addProductFromSearch(item.id.toString())
                              }
                              disabled={
                                !isAvailable && transferAction === 'request'
                              }
                              className={`w-full border-b border-slate-100 px-4 py-2 text-left last:border-b-0 ${
                                !isAvailable && transferAction === 'request'
                                  ? 'cursor-not-allowed bg-red-50 opacity-60'
                                  : 'hover:bg-slate-50'
                              }`}
                            >
                              <p
                                className={`text-sm font-medium ${!isAvailable && transferAction === 'request' ? 'text-red-700' : 'text-slate-900'}`}
                              >
                                {item.name}
                                {!isAvailable &&
                                  transferAction === 'request' && (
                                    <span className="ml-2 text-xs text-red-600">
                                      (No disponible)
                                    </span>
                                  )}
                              </p>
                              <p className="text-xs text-slate-600">
                                Stock: {displayStock} | Lote:{' '}
                                {item.lot || 'N/A'}
                              </p>
                            </button>
                          )
                        })}
                      {inventory.filter(
                        (item) =>
                          item.name
                            .toLowerCase()
                            .includes(productSearchTerm.toLowerCase()) ||
                          (item.lot &&
                            item.lot
                              .toLowerCase()
                              .includes(productSearchTerm.toLowerCase())),
                      ).length === 0 && (
                        <div className="px-4 py-3 text-center text-sm text-slate-600">
                          No se encontraron productos
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Products */}
                <div className="max-h-60 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3">
                  {transferProducts.length > 0 &&
                  transferProducts.some((p) => p.productId) ? (
                    transferProducts
                      .filter((p) => p.productId)
                      .map((product, index) => {
                        const inventoryItem = inventory.find(
                          (i) => i.id.toString() === product.productId,
                        )

                        // Calcular stock disponible según modo y sucursal
                        let displayStock = inventoryItem?.stock || 0
                        if (
                          transferAction === 'request' &&
                          transferData.fromBranch &&
                          inventoryItem
                        ) {
                          if (transferData.fromBranch === 'centro')
                            displayStock = inventoryItem.stockCentro
                          else if (transferData.fromBranch === 'norte')
                            displayStock = inventoryItem.stockNorte
                          else if (transferData.fromBranch === 'sur')
                            displayStock = inventoryItem.stockSur
                        }

                        return (
                          <div
                            key={index}
                            className="flex items-center gap-2 rounded-lg bg-slate-50 p-3"
                          >
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-900">
                                {inventoryItem?.name}
                              </p>
                              <p className="text-xs text-slate-600">
                                Stock disponible
                                {transferAction === 'request' &&
                                transferData.fromBranch
                                  ? ` en ${transferData.fromBranch}`
                                  : ''}
                                : {displayStock}
                              </p>
                            </div>
                            <div className="w-24">
                              <Input
                                type="number"
                                value={product.quantity}
                                onChange={(e) =>
                                  updateTransferProduct(
                                    index,
                                    'quantity',
                                    e.target.value,
                                  )
                                }
                                placeholder="Cant."
                                min="1"
                                max={displayStock}
                              />
                            </div>
                            <Button
                              onClick={() => removeTransferProduct(index)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )
                      })
                  ) : (
                    <p className="py-4 text-center text-sm text-slate-600">
                      Busca y agrega productos usando el campo de búsqueda
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Notes - Only for Send and Request */}
            {(transferAction === 'send' || transferAction === 'request') && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Notas
                  </label>
                  <Textarea
                    value={transferData.notes}
                    onChange={(e) =>
                      setTransferData({
                        ...transferData,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Notas adicionales sobre la transferencia..."
                    rows={3}
                  />
                </div>

                {/* Summary */}
                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="mb-2 text-sm font-medium text-slate-900">
                    Resumen:
                  </p>
                  <div className="space-y-1 text-sm text-slate-700">
                    <p>
                      <strong>Operación:</strong>{' '}
                      {transferAction === 'send'
                        ? 'Enviar productos'
                        : 'Solicitar productos'}
                    </p>
                    <p>
                      <strong>Productos:</strong>{' '}
                      {transferProducts.filter((p) => p.productId).length}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button onClick={handleTransfer} className="flex-1">
                    {transferAction === 'send' && 'Enviar Productos'}
                    {transferAction === 'request' && 'Crear Solicitud'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowTransferModal(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                </div>
              </>
            )}

            {/* Close button for Receive mode */}
            {transferAction === 'receive' && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowTransferModal(false)}
                >
                  Cerrar
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Receive Confirmation Modal */}
      <Dialog
        open={showReceiveConfirmModal}
        onOpenChange={setShowReceiveConfirmModal}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirmar Recepción de Transferencia</DialogTitle>
            <DialogDescription className="sr-only">
              Revisar y confirmar los productos recibidos de la transferencia
            </DialogDescription>
          </DialogHeader>
          {selectedTransfer && (
            <div className="space-y-6">
              {/* Transfer Details */}
              <div className="rounded-lg bg-slate-50 p-4">
                <h4 className="mb-3 text-sm font-medium text-slate-900">
                  Detalles de la Transferencia
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">ID Transferencia:</span>
                    <span className="font-medium text-slate-900">
                      {selectedTransfer.id}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Fecha:</span>
                    <span className="font-medium text-slate-900">
                      {selectedTransfer.date}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Desde:</span>
                    <span className="font-medium text-slate-900">
                      {selectedTransfer.fromBranch}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Hacia:</span>
                    <span className="font-medium text-slate-900">
                      {selectedTransfer.toBranch}
                    </span>
                  </div>
                </div>
              </div>

              {/* Products List */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-900">
                  Productos a Recibir
                </h4>
                <p className="text-xs text-slate-600">
                  Indica el estado de cada producto recibido
                </p>
                <div className="space-y-3">
                  {selectedTransfer.products.map((product, idx) => {
                    const status = receiveProductsStatus[idx.toString()]
                    return (
                      <div
                        key={idx}
                        className="space-y-3 rounded-lg border border-slate-200 p-3"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {product.name}
                            </p>
                            <p className="text-xs text-slate-600">
                              Cantidad solicitada: {product.quantity} unidades
                            </p>
                          </div>
                          <Badge
                            variant="outline"
                            className={
                              status?.status === 'received'
                                ? 'border-green-200 bg-green-50 text-green-700'
                                : 'border-red-200 bg-red-50 text-red-700'
                            }
                          >
                            {status?.status === 'received' && 'Recibido'}
                            {status?.status === 'not_received' && 'No recibido'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => {
                              setReceiveProductsStatus({
                                ...receiveProductsStatus,
                                [idx.toString()]: {
                                  status: 'received',
                                  quantity: product.quantity,
                                  reason: '',
                                },
                              })
                            }}
                            className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                              status?.status === 'received'
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            Recibido
                          </button>
                          <button
                            onClick={() => {
                              setReceiveProductsStatus({
                                ...receiveProductsStatus,
                                [idx.toString()]: {
                                  status: 'not_received',
                                  quantity: 0,
                                  reason: '',
                                },
                              })
                            }}
                            className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                              status?.status === 'not_received'
                                ? 'border-red-500 bg-red-50 text-red-700'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            No recibido
                          </button>
                        </div>

                        {status?.status === 'not_received' && (
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-700">
                              Motivo (del remitente)
                            </label>
                            <Textarea
                              value={status.reason}
                              onChange={(e) => {
                                setReceiveProductsStatus({
                                  ...receiveProductsStatus,
                                  [idx.toString()]: {
                                    ...status,
                                    reason: e.target.value,
                                  },
                                })
                              }}
                              placeholder="Ingresa el motivo..."
                              rows={2}
                              className="text-xs"
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Summary */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="mb-2 text-sm font-medium text-blue-900">
                  Resumen de Recepción
                </p>
                <div className="space-y-1 text-sm text-blue-800">
                  <p>
                    Productos recibidos:{' '}
                    {
                      Object.values(receiveProductsStatus).filter(
                        (v) => v.status === 'received',
                      ).length
                    }
                  </p>
                  {Object.values(receiveProductsStatus).filter(
                    (v) => v.status === 'not_received',
                  ).length > 0 && (
                    <p className="text-red-700">
                      Productos no recibidos:{' '}
                      {
                        Object.values(receiveProductsStatus).filter(
                          (v) => v.status === 'not_received',
                        ).length
                      }
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button onClick={confirmReceive} className="flex-1">
                  Confirmar Recepción
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowReceiveConfirmModal(false)
                    setSelectedTransfer(null)
                    setReceiveProductsStatus({})
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Review Modal */}
      <Dialog open={showSendReviewModal} onOpenChange={setShowSendReviewModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Revisar Solicitud de Transferencia</DialogTitle>
            <DialogDescription className="sr-only">
              Revisar y confirmar qué productos se pueden enviar
            </DialogDescription>
          </DialogHeader>
          {selectedTransfer && (
            <div className="space-y-6">
              {/* Transfer Details */}
              <div className="rounded-lg bg-slate-50 p-4">
                <h4 className="mb-3 text-sm font-medium text-slate-900">
                  Detalles de la Solicitud
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">ID Transferencia:</span>
                    <span className="font-medium text-slate-900">
                      {selectedTransfer.id}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Fecha:</span>
                    <span className="font-medium text-slate-900">
                      {selectedTransfer.date}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Desde:</span>
                    <span className="font-medium text-slate-900">
                      {selectedTransfer.fromBranch}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Hacia:</span>
                    <span className="font-medium text-slate-900">
                      {selectedTransfer.toBranch}
                    </span>
                  </div>
                </div>
              </div>

              {/* Products List */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-900">
                  Productos Solicitados
                </h4>
                <p className="text-xs text-slate-600">
                  Indica qué productos puedes enviar según el stock disponible
                </p>
                <div className="space-y-3">
                  {selectedTransfer.products.map((product, idx) => {
                    const status = sendProductsStatus[idx.toString()]
                    const inventoryItem = inventory.find(
                      (i) => i.name === product.name,
                    )
                    const availableStock = inventoryItem?.stock || 0

                    return (
                      <div
                        key={idx}
                        className="space-y-3 rounded-lg border border-slate-200 p-3"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {product.name}
                            </p>
                            <p className="text-xs text-slate-600">
                              Solicitado: {product.quantity} unidades
                            </p>
                            <p className="text-xs text-slate-600">
                              Stock disponible:{' '}
                              <span
                                className={
                                  availableStock >= product.quantity
                                    ? 'font-medium text-green-600'
                                    : 'font-medium text-red-600'
                                }
                              >
                                {availableStock} unidades
                              </span>
                            </p>
                          </div>
                          <Badge
                            variant="outline"
                            className={
                              status?.status === 'complete'
                                ? 'border-green-200 bg-green-50 text-green-700'
                                : status?.status === 'partial'
                                  ? 'border-yellow-200 bg-yellow-50 text-yellow-700'
                                  : 'border-red-200 bg-red-50 text-red-700'
                            }
                          >
                            {status?.status === 'complete' && 'Envío completo'}
                            {status?.status === 'partial' && 'Envío parcial'}
                            {status?.status === 'not_sent' && 'No se envía'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          <button
                            onClick={() => {
                              setSendProductsStatus({
                                ...sendProductsStatus,
                                [idx.toString()]: {
                                  status: 'complete',
                                  quantity: product.quantity,
                                  reason: '',
                                },
                              })
                            }}
                            disabled={availableStock < product.quantity}
                            className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                              status?.status === 'complete'
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : availableStock < product.quantity
                                  ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                  : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            Completo
                          </button>
                          <button
                            onClick={() => {
                              setSendProductsStatus({
                                ...sendProductsStatus,
                                [idx.toString()]: {
                                  status: 'partial',
                                  quantity: Math.min(
                                    availableStock,
                                    product.quantity,
                                  ),
                                  reason: '',
                                },
                              })
                            }}
                            disabled={availableStock === 0}
                            className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                              status?.status === 'partial'
                                ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                                : availableStock === 0
                                  ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                  : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            Parcial
                          </button>
                          <button
                            onClick={() => {
                              setSendProductsStatus({
                                ...sendProductsStatus,
                                [idx.toString()]: {
                                  status: 'not_sent',
                                  quantity: 0,
                                  reason:
                                    availableStock === 0
                                      ? 'Sin stock disponible'
                                      : '',
                                },
                              })
                            }}
                            className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                              status?.status === 'not_sent'
                                ? 'border-red-500 bg-red-50 text-red-700'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            No enviar
                          </button>
                        </div>

                        {status?.status === 'partial' && (
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-700">
                              Cantidad a Enviar
                            </label>
                            <Input
                              type="number"
                              min={0}
                              max={Math.min(availableStock, product.quantity)}
                              value={status.quantity}
                              onChange={(e) => {
                                const qty = parseInt(e.target.value) || 0
                                setSendProductsStatus({
                                  ...sendProductsStatus,
                                  [idx.toString()]: {
                                    ...status,
                                    quantity: qty,
                                  },
                                })
                              }}
                              className="h-8"
                            />
                          </div>
                        )}

                        {(status?.status === 'not_sent' ||
                          status?.status === 'partial') && (
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-700">
                              Motivo
                            </label>
                            <Textarea
                              value={status.reason}
                              onChange={(e) => {
                                setSendProductsStatus({
                                  ...sendProductsStatus,
                                  [idx.toString()]: {
                                    ...status,
                                    reason: e.target.value,
                                  },
                                })
                              }}
                              placeholder="Indica por qué no se puede enviar completo..."
                              rows={2}
                              className="text-xs"
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Summary */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="mb-2 text-sm font-medium text-blue-900">
                  Resumen del Envío
                </p>
                <div className="space-y-1 text-sm text-blue-800">
                  <p>
                    Productos completos:{' '}
                    {
                      Object.values(sendProductsStatus).filter(
                        (v) => v.status === 'complete',
                      ).length
                    }
                  </p>
                  <p>
                    Productos parciales:{' '}
                    {
                      Object.values(sendProductsStatus).filter(
                        (v) => v.status === 'partial',
                      ).length
                    }
                  </p>
                  {Object.values(sendProductsStatus).filter(
                    (v) => v.status === 'not_sent',
                  ).length > 0 && (
                    <p className="text-red-700">
                      Productos no enviados:{' '}
                      {
                        Object.values(sendProductsStatus).filter(
                          (v) => v.status === 'not_sent',
                        ).length
                      }
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button onClick={confirmSend} className="flex-1">
                  Confirmar Envío
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSendReviewModal(false)
                    setSelectedTransfer(null)
                    setSendProductsStatus({})
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Importar Productos desde Excel</DialogTitle>
            <DialogDescription className="sr-only">
              Cargar productos masivamente desde un archivo Excel
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-2 text-sm font-medium text-blue-900">
                Instrucciones:
              </p>
              <ol className="list-inside list-decimal space-y-1 text-sm text-blue-800">
                <li>Descarga la plantilla de Excel</li>
                <li>Completa los datos de los productos</li>
                <li>Sube el archivo completo</li>
              </ol>
            </div>

            <Button variant="outline" className="w-full">
              <Download className="mr-2 h-4 w-4" />
              Descargar Plantilla Excel
            </Button>

            <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                id="excel-upload"
              />
              <label htmlFor="excel-upload" className="cursor-pointer">
                <Upload className="mx-auto mb-2 h-12 w-12 text-slate-400" />
                <p className="text-sm text-slate-600">
                  Haz clic para seleccionar archivo
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Solo archivos .xlsx o .xls
                </p>
              </label>
            </div>

            <Button className="w-full">Importar Productos</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
